// Router
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

// Third party
import { useMutation } from '@tanstack/react-query';
import jwt_decode from 'jwt-decode';
import { Crisp } from 'crisp-sdk-web';

import { useSnackbar } from 'notistack';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { setSessionVal, hashPassword } from '@mgm/common/utils';
import get from 'lodash/get';
import { usePostHog } from 'posthog-js/react';
import { LoginController } from '../../services';
import { useAppDispatch, useAppSelector } from '../useAppStore';
import { authActions } from '../../store/authenticate/slice';

// Data
import { DEFAULT_APPEARANCE, ROUTES, TOKEN_KEY } from '../../data';

// Types
import { TAxiosError, TGetSettingsRes, TLoginRes } from '../../types/responses';
import { TLoginRequest } from '../../types/requests';

// Hooks
import { useCommonAPI, useUpdateUserAPI } from './useCommon';
import { useGetTeamAPI } from './useTeam';
import { useGetSettingsAPI } from './useSettings';
import { TTokenDetails } from '../../store/authenticate/types';

import { getSelectedLang } from '../../store/common/selectors';
import { useError } from '../useCommon';
import { MGM_ADMIN } from '../../../config';
import { useCrisp } from '../../lib/hooks';
import { TSettings } from '../../types';
import { ordersActions } from '../../store/orders/slice';
import { TSettingPermissions } from '../../types/settings.type';
import { useGetTeamsDataAPI } from './useCommissions';
import { useAuth } from '../useAuth';
import { getAuthenticateUser } from '../../store/authenticate/selectors';
import { useFeature } from '../useFeature';
import { getManagerPermissions } from '../../store/settings/selectors';

const loginApi = async (options: TLoginRequest) => {
  const {
    email,
    password,
    shopId,
    shopName,
    isHashPassword = true,
  } = options;

  const data = {
    email: email?.toLowerCase(),
    password: isHashPassword ? hashPassword(password) : password,
    // password,
    ...(shopName && { shopName }),
    ...(shopId && { shopId }),
  };

  const rsp = await LoginController().login<TLoginRes>(data);

  if (rsp?.success && 'data' in rsp) setSessionVal(TOKEN_KEY, rsp.data.authToken, 'sessionStorage');

  return rsp;
};
export function useLoginAPI() {
  const dispatch = useAppDispatch();
  const selectedLang = useAppSelector(getSelectedLang);
  const { t } = useTranslation('welcome');
  const posthog = usePostHog();

  const navigation = useNavigate();
  const { getPlatformData } = useCommonAPI();
  const { enqueueSnackbar } = useSnackbar();
  const { refetch: teamRefetch } = useGetTeamAPI();
  const { refetch: teamCommissionRefetch } = useGetTeamsDataAPI();
  const { loadLocations } = useCommonAPI();

  const settings = useAppSelector(getManagerPermissions);
  const { mutateAsync: mutateSettings } = useGetSettingsAPI();
  const { mutateAsync: mutateUpdateUser } = useUpdateUserAPI();
  const { handleReqError } = useError();
  const { setCrispSession } = useCrisp();
  const { getIdToken } = useAuth();
  const { personnel, shop } = useAppSelector(getAuthenticateUser);

  const { allowFeatureAccess } = useFeature();

  useEffect(() => {
    if (personnel?.email && personnel?.shopName && getIdToken()) {
      const canManageCommissionPlans = get(settings, 'actions.commissionPlans.manage');

      teamRefetch();
      if (allowFeatureAccess('teamCommissions') && canManageCommissionPlans) teamCommissionRefetch();
      dispatch(ordersActions.setDorChosenDate(moment.tz(shop?.shopTimezone).startOf('day').format()));
      dispatch(ordersActions.setOrdersChosenDates(
        [
          moment.tz(shop?.shopTimezone).startOf('week').startOf('day'),
          moment.tz(shop?.shopTimezone).endOf('week').endOf('day'),
        ],
      ));
    }
  }, [personnel, getIdToken, shop, settings]);

  const determineNavigationPath = (permissionLevel: string, settings: TSettings) => {
    let visibility = {} as TSettingPermissions['visibility'];

    // Use employeePermissions for employees and managerPermissions for managers
    if (permissionLevel === 'Employee') {
      visibility = settings.employeePermissions?.visibility || {};
    } else if (permissionLevel === 'Manager') {
      visibility = settings.managerPermissions?.visibility || {};
    }

    // Admins should always navigate to /team
    if (permissionLevel === 'Admin') {
      return '/team';
    }

    // Determine paths based on visibility for Manager or Employee
    const visibilityPaths = {
      dashboard: visibility?.dashboard?.view ? '/dashboard' : null,
      schedules: visibility?.schedules?.view ? ROUTES.WORK_SCHEDULES : null,
      payroll: visibility?.payroll?.view ? '/payroll' : null,
      tasklists: visibility?.tasklists?.view ? '/tasklists' : null,
      timesheets: visibility?.sheets?.view ? '/timesheets' : null,
      commissionPlans: visibility?.commissionPlans?.view ? ROUTES.COMMISSION_PLANS : null,
      commissionSales: visibility?.commissionSales?.view ? ROUTES.COMMISSION_SALES : null,
    };

    // Find the first visible path or fallback to /team
    return Object.values(visibilityPaths).find((path) => path) || '/help';
  };

  return useMutation(loginApi, {
    onSuccess: async (rsp, variables) => {
      if (rsp.success && 'data' in rsp) {
        const authToken = rsp.data.authToken || '';

        const userData = { ...rsp.data };

        if (variables.isDisplayToast) {
          enqueueSnackbar(`${t('loginSuccess')}`, {
            variant: 'success',
          });
        }

        if (!rsp.data?.user?.settings) {
          userData.user = {
            settings: {
              ui: DEFAULT_APPEARANCE,
            },
            name: userData.personnel.name,
            phone: userData.personnel?.phone?.e164 || '',
          };
        }

        dispatch(authActions.setAuthenticateUser(userData));

        const tokenDetails: TTokenDetails = jwt_decode(authToken);

        dispatch(authActions.setTokenDetails({
          ...tokenDetails,
          settings: {
            ...tokenDetails.settings,
            ui: {
              ...tokenDetails.settings.ui,
              locale: selectedLang || tokenDetails.settings?.ui?.locale || DEFAULT_APPEARANCE.locale,
            },
          },
        }));

        dispatch(ordersActions.setDailyStateSelectedLocation(userData.personnel?.locations[0]?.id || 0));

        getPlatformData(rsp.data.shop.shopName);
        const settingsRsp = await mutateSettings(rsp.data.personnel.shopName) as TGetSettingsRes;

        if (selectedLang) {
          await mutateUpdateUser({
            settings: {
              ui: {
                theme: tokenDetails.settings.ui?.theme,
                locale: selectedLang,
              },
            },
            name: rsp.data.personnel.name,
            phone: rsp.data.personnel.phone?.e164 || '',
          });
        }

        await i18n.changeLanguage(selectedLang || tokenDetails.settings?.ui?.locale || DEFAULT_APPEARANCE.locale);

        if (!variables.isAdmin && !variables.isRegistered) {
          setCrispSession({
            email: variables.email ?? '',
            name: rsp.data.personnel.name,
          });

          Crisp.session.setSegments(
            ['Shopify',
              userData.personnel.permissionLevel, selectedLang || tokenDetails.settings?.ui?.locale],
            true,
          );

          Crisp.session.setData({
            permission: rsp.data.personnel.permissionLevel,
            shopName: rsp.data.shop.shopName,
            adminSearchShop: `${MGM_ADMIN}?shopName=${rsp.data.shop.shopName}`,
            adminSearchUser: `${MGM_ADMIN}?email=${variables.email}`,
            adminLoginToUser: `${MGM_ADMIN}?email=${variables.email}&action=login`,
            adminUserClockIn: `${MGM_ADMIN}?email=${variables.email}&action=clockIn`,
            adminUserClockOut: `${MGM_ADMIN}?email=${variables.email}&action=clockOut`,
          });

          posthog.identify(variables.email, {
            email: variables.email,
            shopName: rsp.data.shop?.shopName,
            plan: tokenDetails.subscription?.subscriptionPlanName,
          });

          posthog.capture('ManageMate_web', {
            email: variables?.email,
            shopName: rsp.data.shop?.shopName,
            action: 'user_logged_in',
          });
        }

        if (variables?.isRegistered) {
          if (tokenDetails.subscription?.subscriptionPlanName === 'free') {
            navigation('/team');
          } else {
            navigation('/onboarding/invite-team');
          }
        }

        loadLocations.mutate();

        if (variables?.isRedirect) {
          const navigateTo = variables.redirectTo
            ? variables.redirectTo : determineNavigationPath(userData.personnel.permissionLevel, settingsRsp.settings);
          navigation(navigateTo);
        }
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, variables);
        Crisp.trigger.run('trouble-signing-in');
      } else if ('msg' in rsp) {
        console.error(`ERROR! login request failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! login request threw an Exception! ${error}`);
    },
  });
}
